import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import MainLayout from "../components/mainLayout";
import { api } from "../api";
import { showError, showInfo } from "../utils/toast";
import {
  ArrowLeft,
  Save,
  X,
  Building2,
  User,
  Mail,
  Phone,
  Tag as TagIcon,
  DollarSign,
  CalendarClock,
  MessageSquare,
  HelpCircle,
} from "lucide-react";
import HelpBar from "../components/HelpBar";
import HelpModal from "../components/HelpModal";
import "../sass/pages/leadNew.scss";

// Mesmos valores aceitos no backend
const CanalOpts = [
  { value: "SITE",      label: "Site" },
  { value: "WHATSAPP",  label: "WhatsApp" },
  { value: "PHONE",     label: "Telefone" },
  { value: "INDICACAO", label: "Indicação" },
  { value: "OUTRO",     label: "Outro" },
];

export default function LeadNew() {
  const navigate = useNavigate();

  const meId = useMemo(() => {
    const a = Number(sessionStorage.getItem("IdFuncionario") ?? 0) || 0;
    const b = Number(sessionStorage.getItem("codFunc") ?? 0) || 0;
    return a || b || null;
  }, []);

  const [helpOpen, setHelpOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    nome: "",
    contatoNome: "",
    contatoEmail: "",
    contatoTel: "",
    canal: "",
    valorEstimado: "",
    tags: "",
    descricao: "",
    dtFechPrev: "",
  });

  const onChange = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const toNumberOrNull = (v) => {
    if (v === "" || v == null) return null;
    const s = String(v).replace(/\./g, "").replace(",", ".");
    const n = Number(s);
    return Number.isFinite(n) ? n : null;
  };

  const validateClient = () => {
    if (!form.nome.trim()) return "Informe o nome da empresa/organização.";
    if (form.contatoEmail && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.contatoEmail))
      return "E-mail do contato inválido.";
    if (form.canal && !CanalOpts.some((c) => c.value === form.canal))
      return "Canal inválido.";
    return null;
  };

  const buildPayload = () => ({
    nome: form.nome.trim(),
    contatoNome: form.contatoNome?.trim() || "",
    contatoEmail: form.contatoEmail?.trim() || "",
    contatoTel: form.contatoTel?.trim() || "",
    canal: form.canal || null,
    valorEstimado: toNumberOrNull(form.valorEstimado),
    tags: form.tags?.trim() || "",
    descricao: form.descricao?.trim() || "",
    dtFechPrev: form.dtFechPrev ? new Date(form.dtFechPrev).toISOString() : null,
    createdBy: typeof meId === "number" ? meId : undefined,
  });

  const salvar = async () => {
    const v = validateClient();
    if (v) { showError(v); return; }

    setSaving(true);
    try {
      const { data } = await api.post("/leads", buildPayload());
      showInfo("Lead criado com sucesso.");
      if (data?.ID_LEAD) navigate(`/leads/${data.ID_LEAD}`); else navigate("/leads");
    } catch (e) {
      const joiFirst =
        Array.isArray(e?.response?.data?.details) &&
        e.response.data.details[0]?.message;
      const msg =
        joiFirst ||
        e?.response?.data?.message ||
        e?.response?.data?.detail ||
        "Falha ao criar lead.";
      showError(msg);
    } finally {
      setSaving(false);
    }
  };

  const cancelar = () => navigate("/leads");

  // Atalhos: ? (ajuda), s (salvar), Esc (voltar)
  useEffect(() => {
    const onKey = (e) => {
      const tag = (e.target?.tagName || "").toLowerCase();
      const isField = ["input", "select", "textarea"].includes(tag);
      if (e.key === "?" && !isField) { e.preventDefault(); setHelpOpen((v) => !v); }
      if (e.key.toLowerCase() === "s" && !isField) { e.preventDefault(); salvar(); }
      if (e.key === "Escape") { e.preventDefault(); cancelar(); }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [salvar]);

  return (
    <MainLayout>
      <div className="leads-page lead-new">
        <div className="leads-toolbar">
          <div className="left-actions">
            <button
              className="btn ghost"
              onClick={() => navigate(-1)}
              aria-label="Voltar"
              title="Voltar (Esc)"
            >
              <ArrowLeft size={16} />
              <span>Voltar</span>
            </button>
          </div>
          <div className="leads-actions">
            <button
              className="btn ghost"
              onClick={() => setHelpOpen(true)}
              aria-label="Ajuda"
              title="Ajuda (atalho: ?)"
            >
              <HelpCircle size={16} />
              <span>Ajuda</span>
            </button>
            <button className="btn ghost" onClick={cancelar} title="Cancelar (Esc)">
              <X size={16} />
              <span>Cancelar</span>
            </button>
            <button
              className="btn primary"
              onClick={salvar}
              disabled={saving || !form.nome.trim()}
              title="Salvar (atalho: S)"
            >
              <Save size={16} />
              <span>{saving ? "Salvando..." : "Salvar"}</span>
            </button>
          </div>
        </div>

        <HelpBar storageKey="leadnew_helpbar_dismissed">
          <b>Como preencher:</b> informe <i>Empresa/Org.</i>, dados do <i>Contato</i> e, se tiver,
          <i> Canal</i>, <i>Valor Estimado</i> e <i>Fechamento Previsto</i>. Clique em <b>Salvar</b> para criar o lead.
          Atalhos: <code>?</code> ajuda, <code>S</code> salvar, <code>Esc</code> voltar.
        </HelpBar>

        <div className="leads-content">
          <div className="card">
            <div className="card-header">
              <h3>Novo Lead</h3>
              <div className="muted" style={{ display: "flex", alignItems: "center", gap: 6 }}>
                <CalendarClock size={14} />
                {new Date().toLocaleString("pt-BR")}
              </div>
            </div>

            <div className="card-body">
              <div className="form-grid">
                <div className="field">
                  <label><Building2 size={14} /> Empresa/Org. <span className="req">*</span></label>
                  <input
                    value={form.nome}
                    onChange={(e) => onChange("nome", e.target.value)}
                    placeholder="Empresa ou organização"
                    autoFocus
                  />
                </div>

                <div className="field">
                  <label><User size={14} /> Contato</label>
                  <input
                    value={form.contatoNome}
                    onChange={(e) => onChange("contatoNome", e.target.value)}
                    placeholder="Nome do contato"
                  />
                </div>

                <div className="field">
                  <label><Mail size={14} /> E-mail</label>
                  <input
                    value={form.contatoEmail}
                    onChange={(e) => onChange("contatoEmail", e.target.value)}
                    placeholder="email@dominio.com"
                    inputMode="email"
                  />
                </div>

                <div className="field">
                  <label><Phone size={14} /> Telefone</label>
                  <input
                    value={form.contatoTel}
                    onChange={(e) => onChange("contatoTel", e.target.value)}
                    placeholder="(00) 00000-0000"
                  />
                </div>

                <div className="field">
                  <label><TagIcon size={14} /> Canal</label>
                  <div className="select-icon">
                    <TagIcon size={16} />
                    <select
                      value={form.canal}
                      onChange={(e) => onChange("canal", e.target.value)}
                    >
                      <option value="">Selecione…</option>
                      {CanalOpts.map((c) => (
                        <option key={c.value} value={c.value}>{c.label}</option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="field">
                  <label><DollarSign size={14} /> Valor Estimado</label>
                  <input
                    value={form.valorEstimado}
                    onChange={(e) => onChange("valorEstimado", e.target.value)}
                    placeholder="0,00"
                    inputMode="decimal"
                  />
                </div>

                <div className="field">
                  <label><CalendarClock size={14} /> Fechamento Previsto</label>
                  <input
                    type="date"
                    value={form.dtFechPrev}
                    onChange={(e) => onChange("dtFechPrev", e.target.value)}
                  />
                </div>

                <div className="field span-2">
                  <label><TagIcon size={14} /> Tags</label>
                  <input
                    value={form.tags}
                    onChange={(e) => onChange("tags", e.target.value)}
                    placeholder="tag1, tag2, tag3"
                  />
                </div>

                <div className="field span-2">
                  <label><MessageSquare size={14} /> Descrição</label>
                  <textarea
                    rows={4}
                    value={form.descricao}
                    onChange={(e) => onChange("descricao", e.target.value)}
                    placeholder="Contexto do lead, dores, necessidades…"
                  />
                </div>
              </div>
            </div>

            <div className="card-footer" style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
              <button className="btn ghost" onClick={cancelar} title="Cancelar (Esc)">
                <X size={16} />
                <span>Cancelar</span>
              </button>
              <button
                className="btn primary"
                onClick={salvar}
                disabled={saving || !form.nome.trim()}
                title="Salvar (atalho: S)"
              >
                <Save size={16} />
                <span>{saving ? "Salvando..." : "Salvar"}</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      <HelpModal open={helpOpen} onClose={() => setHelpOpen(false)} />
    </MainLayout>
  );
}
